import { toBackendLang } from '@/nova/config/langCodeMap';

import type { ArtStyleOption, ColorOption, Theme, ThemeScreenshot } from '../lib/domain-types';

/**
 * Build the preview iframe URL from the current selection.
 *
 * Example:
 *   https://betsphere-7ccb9aff.spark-test.newtoncelestial.com/
 *     ?wsid=<theme.variantId>
 *     &wsp=
 *     &wst=<color.designThemeId>
 *     &site_art_style_banner_set_id=<style.sets.banner>
 *     &site_art_style_decorations_set_id=<style.sets.decorations>
 *     &site_art_style_iconography_set_id=<style.sets.iconography>
 *     &site_game_thumbnail_set_id=<style.sets.gameThumbnail>
 *     &lang=<lang>
 *     &nsc=1
 *
 * `lang` (the active UI language, e.g. `mx`) is forwarded so the framed casino site
 * loads in the same language as the rest of the studio interface. It is passed only
 * for the live, on-screen preview; the URL persisted into `configuration_data` omits
 * it (that stored URL is a saved config value, not a per-session UI preference).
 *
 * `nsc=1` asks the framed site to hide its own scrollbar, so the studio no longer
 * over-sizes and clips the preview iframe to keep that scrollbar out of view. Like
 * `lang` it is a display-only concern: set only for the live preview, omitted from
 * the persisted URL.
 */
export function buildPreviewUrl(
  theme: Theme | null,
  color: ColorOption | null,
  style: ArtStyleOption | null,
  lang?: string,
  noScrollbar?: boolean,
): string {
  if (!theme?.previewUrl) return '';

  let url: URL;
  try {
    url = new URL(theme.previewUrl);
  } catch {
    return '';
  }

  // Only ever frame an http(s) preview. `new URL()` happily parses `javascript:`,
  // `data:` and `blob:` URLs; a backend/CMS-set `preview_url` of one of those would
  // otherwise reach an <iframe src> (and `window.open`) and run script in the studio
  // origin. Reject any non-http(s) scheme here, the single source for every preview iframe.
  if (url.protocol !== 'http:' && url.protocol !== 'https:') return '';

  const p = url.searchParams;
  p.set('wsid', theme.variantId);
  p.set('wsp', '');
  if (color) p.set('wst', color.designThemeId);
  if (style) {
    p.set('site_art_style_banner_set_id', style.sets.banner);
    p.set('site_art_style_decorations_set_id', style.sets.decorations);
    p.set('site_art_style_iconography_set_id', style.sets.iconography);
    p.set('site_game_thumbnail_set_id', style.sets.gameThumbnail);
  }
  if (lang) p.set('lang', lang);
  if (noScrollbar) p.set('nsc', '1');
  return url.toString();
}

/**
 * Finds the backend-generated screenshot set for the Finish-step device group: the
 * `theme.screenshots` entry matching the selected colour (skin) and art style, in the
 * active UI language, falling back to `en` (guaranteed generated) and then to
 * whichever locale exists for that combination. Returns `null` when the backend has
 * not generated ANY capture yet for this (theme, colour, style) triple, in which case
 * the caller should fall back to the live iframe.
 */
export function findThemeScreenshot(
  theme: Theme | null,
  color: ColorOption | null,
  style: ArtStyleOption | null,
  lang: string,
): ThemeScreenshot | null {
  if (!theme || !color || !style) return null;

  const matches = theme.screenshots.filter((s) => s.skinId === color.id && s.artStyleId === style.id);
  if (matches.length === 0) return null;

  // `screenshot.locale` is the BACKEND art_bundle code (e.g. br/ar/af); convert the app UI `lang`
  // (bra/arg/za) before matching, else a non-passthrough locale never matches and falls back to `en`.
  const backendLang = toBackendLang(lang);
  return matches.find((s) => s.locale === backendLang) ?? matches.find((s) => s.locale === 'en') ?? matches[0];
}
